import { useState, useEffect, useMemo, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { useStores } from "@/stores";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Loader2, Trash2, Plus, Settings2, Expand } from "lucide-react";
import { toast } from "sonner";
import { CreateCategoryDialog } from "./CreateCategoryDialog";
import { ManageCategorySubcategoriesDialog } from "./ManageCategorySubcategoriesDialog";
import { EditDescriptionModal } from "./EditDescriptionModal";

// Simple category types for document categories (Image or Text based)
const SIMPLE_CATEGORY_TYPES = [
  { value: "image", label: "Image" },
  { value: "text", label: "Text" },
];

interface CategoryUpdate {
  category_name: string;
  category_type: string;
  description: string | null;
}

const CategoriesTable = observer(() => {
  const { documentCategoriesStore } = useStores();
  const [categoryUpdates, setCategoryUpdates] = useState<Record<number, CategoryUpdate>>({});
  const [savingCategory, setSavingCategory] = useState<number | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<number | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [subcategoryDialogParentId, setSubcategoryDialogParentId] = useState<number | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<{ id: number; name: string } | null>(
    null
  );
  const [subcategoryCounts, setSubcategoryCounts] = useState<Record<number, number>>({});
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(null);
  const [editingDescription, setEditingDescription] = useState<string | null>(null);

  const subcategoryParentCategory = useMemo(() => {
    if (!subcategoryDialogParentId) return null;
    return documentCategoriesStore.getById(subcategoryDialogParentId) as any;
  }, [subcategoryDialogParentId, documentCategoriesStore]);

  useEffect(() => {
    documentCategoriesStore.loadCategories();
  }, [documentCategoriesStore]);

  // Load subcategory counts for each top-level category
  useEffect(() => {
    const loadSubcategoryCounts = async () => {
      const counts: Record<number, number> = {};
      for (const category of documentCategoriesStore.categoriesList) {
        try {
          const subcategories = await documentCategoriesStore.loadSubcategories(category.id);
          counts[category.id] = subcategories.length;
        } catch {
          counts[category.id] = 0;
        }
      }
      setSubcategoryCounts(counts);
    };

    if (documentCategoriesStore.categoriesList.length > 0) {
      loadSubcategoryCounts();
    }
  }, [documentCategoriesStore, documentCategoriesStore.categoriesList.length]);

  useEffect(() => {
    // Initialize local state with current categories
    const updates: Record<number, CategoryUpdate> = {};
    documentCategoriesStore.categoriesList.forEach((category) => {
      updates[category.id] = {
        category_name: category.category_name,
        category_type: category.category_type,
        description: category.description,
      };
    });
    setCategoryUpdates(updates);
  }, [documentCategoriesStore.categoriesList]);

  const handleSave = async (categoryId: number) => {
    const update = categoryUpdates[categoryId];
    if (!update) return;

    setSavingCategory(categoryId);
    try {
      await documentCategoriesStore.updateCategory(categoryId, update);
      toast.success("Category updated successfully");
    } catch (error) {
      toast.error("Failed to update category");
    } finally {
      setSavingCategory(null);
    }
  };

  const handleDeleteClick = useCallback((categoryId: number, categoryName: string) => {
    setCategoryToDelete({ id: categoryId, name: categoryName });
    setDeleteConfirmOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!categoryToDelete) return;

    setDeletingCategory(categoryToDelete.id);
    try {
      await documentCategoriesStore.deleteCategory(categoryToDelete.id);
      toast.success("Category deleted successfully");
    } catch (error) {
      toast.error("Failed to delete category");
    } finally {
      setDeletingCategory(null);
      setDeleteConfirmOpen(false);
      setCategoryToDelete(null);
    }
  }, [categoryToDelete, documentCategoriesStore]);

  const handleFieldChange = (
    categoryId: number,
    field: keyof CategoryUpdate,
    value: string | null
  ) => {
    setCategoryUpdates((prev) => ({
      ...prev,
      [categoryId]: {
        ...prev[categoryId],
        [field]: value,
      },
    }));
  };

  // Filter categories based on search query (memoized for performance)
  // MUST be before early returns to comply with React Rules of Hooks
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) {
      return documentCategoriesStore.categoriesList;
    }

    const query = searchQuery.toLowerCase();
    return documentCategoriesStore.categoriesList.filter((category) => {
      return (
        category.category_name.toLowerCase().includes(query) ||
        category.category_type.toLowerCase().includes(query) ||
        (category.description && category.description.toLowerCase().includes(query))
      );
    });
  }, [documentCategoriesStore.categoriesList, searchQuery]);

  // Early returns AFTER all hooks
  if (documentCategoriesStore.isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (documentCategoriesStore.error) {
    return (
      <div className="rounded-md bg-destructive/10 p-4 text-destructive">
        Error loading categories: {documentCategoriesStore.error}
      </div>
    );
  }

  if (filteredCategories.length === 0) {
    return (
      <>
        <div className="mb-4 flex items-center justify-between">
          <Input
            placeholder="Search categories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1"
          />
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create New Category
          </Button>
        </div>
        <div className="rounded-md border border-dashed p-8 text-center text-muted-foreground">
          {searchQuery.trim()
            ? "No categories match your search."
            : "No categories found. Click the button above to create your first category."}
        </div>
        <CreateCategoryDialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen} />
      </>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Input
          placeholder="Search categories..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1"
        />
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create New Category
        </Button>
      </div>

      <div className="rounded-md border">
        <Table className="w-full" style={{ tableLayout: "fixed" }}>
          <TableHeader>
            <TableRow>
              <TableHead style={{ width: "25%" }}>Category Name</TableHead>
              <TableHead style={{ width: "20%" }}>Type</TableHead>
              <TableHead style={{ width: "40%" }}>Description</TableHead>
              <TableHead style={{ width: "15%" }} className="text-right">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredCategories.map((category) => {
              const update = categoryUpdates[category.id];
              if (!update) return null;

              const hasChanges =
                update.category_name !== category.category_name ||
                update.category_type !== category.category_type ||
                update.description !== category.description;

              return (
                <TableRow key={category.id}>
                  <TableCell style={{ width: "25%" }}>
                    <Input
                      value={update.category_name}
                      onChange={(e) =>
                        handleFieldChange(category.id, "category_name", e.target.value)
                      }
                      className="h-8"
                    />
                  </TableCell>
                  <TableCell style={{ width: "20%" }}>
                    <Select
                      value={update.category_type}
                      onValueChange={(value) =>
                        handleFieldChange(category.id, "category_type", value)
                      }
                    >
                      <SelectTrigger className="h-8 w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SIMPLE_CATEGORY_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell style={{ width: "40%" }}>
                    <div className="flex items-center gap-1">
                      <span className="min-w-0 flex-1 truncate text-sm">
                        {update.description || (
                          <span className="italic text-muted-foreground">No description</span>
                        )}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 shrink-0 p-0"
                        onClick={() => {
                          setEditingCategoryId(category.id);
                          setEditingDescription(update.description);
                          setIsDescriptionModalOpen(true);
                        }}
                        title="Edit description"
                      >
                        <Expand className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell style={{ width: "15%" }} className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSubcategoryDialogParentId(category.id)}
                        title="Manage subcategories"
                        className="relative"
                      >
                        <Settings2 className="h-4 w-4" />
                        {subcategoryCounts[category.id] > 0 && (
                          <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-red-500" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSave(category.id)}
                        disabled={!hasChanges || savingCategory === category.id}
                      >
                        {savingCategory === category.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          "Save"
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteClick(category.id, category.category_name)}
                        disabled={deletingCategory === category.id}
                      >
                        {deletingCategory === category.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <CreateCategoryDialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen} />

      <ManageCategorySubcategoriesDialog
        open={subcategoryDialogParentId !== null}
        onOpenChange={(open) => {
          if (!open) setSubcategoryDialogParentId(null);
        }}
        parentCategory={subcategoryParentCategory}
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        onConfirm={handleConfirmDelete}
        title="Delete Category"
        description={`Are you sure you want to delete "${categoryToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="destructive"
      />

      <EditDescriptionModal
        open={isDescriptionModalOpen}
        onOpenChange={setIsDescriptionModalOpen}
        title="Edit Category Description"
        description={editingDescription}
        itemName={
          editingCategoryId !== null ? categoryUpdates[editingCategoryId]?.category_name : undefined
        }
        onSave={(newDescription) => {
          if (editingCategoryId !== null) {
            handleFieldChange(editingCategoryId, "description", newDescription);
          }
        }}
      />
    </div>
  );
});

export default CategoriesTable;
